import React, { useEffect, useState, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Tooltip,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  ClickAwayListener,
  Select,
  Drawer,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import { PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import {
  FilterList as FilterIcon,
  Add as AddIcon,
  GetApp as ExportIcon,
  Visibility as ViewIcon,
  Fingerprint as FingerprintIcon,
  AccountBalance as BankIcon,
  Search as SearchIcon,
  ContentCopy as ContentCopyIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { SuperadminTransactionSummaryCards } from '../../components';
import {
  SUPERADMIN_TRANSACTION_STATUSES,
  getSuperadminStatusColor,
  getSuperadminStatusLabel,
} from '../../constants';
import type { SuperadminTransaction, SuperadminTransactionStats } from '../../types';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { formatCurrency } from '@/utils/formatters';
import { format as formatMomentDate } from '@/utils/time';
import { superadminTransactionsService } from '../../../../services/transactionsService';
import { toast } from 'react-hot-toast';
import { FilterPanel } from '../../components/TransactionFilters/FilterPanel';
import { useSuperadminAuth } from '@/superadmin/contexts/SuperadminAuthContext';
// import { start } from 'repl';
// import { relative } from 'path';
// import { positions } from '@mui/system';

export const SuperadminTransactionsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [transactions, setTransactions] = useState<SuperadminTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  // const [merchantTransactionSearchTerm, setMerchantTransactionSearchTerm] = useState('');

  const [searchKey, setSearchKey] = useState<string>('transaction_id');
  const [searchValue, setSearchValue] = useState<string>('');

  const [stats, setStats] = useState<SuperadminTransactionStats | null>(null);
  const [merchants, setMerchants] = useState<any[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15); // Default page size

  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filters, setFilters] = useState<TransactionFilters>({
    searchTerm: '',
    statusFilter: '',
    merchants: [],
    dateRange: { startDate: '', endDate: '' },
  });

  const { user } = useSuperadminAuth();

  if (!user) return <div>No Access</div>;

  const hasPermission = (permission: string) => {
    if (user.is_superuser) return true; // superuser has everything
    return user.permission_list?.includes(permission);
  };

  // const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // const toggleFilterDrawer = (open: boolean) => () => {
  //   setIsFilterDrawerOpen(open);
  // };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTransaction(null);
  };

  // Applied filters for API calls
  const [appliedFilters, setAppliedFilters] = useState<{
    transaction_id?: string;
    merchant_transaction_id?: string;
    transaction_status?: string;
    start_date?: string;
    end_date?: string;
    merchant_uuid?: string;
  }>({});

  // Load transactions with API integration
 useEffect(() => {
  fetchTransactions();
  fetchTransactionStats();
  fetchMerchants();
}, [currentPage, pageSize, appliedFilters]);


  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await superadminTransactionsService.list(
        currentPage,
        pageSize,
        appliedFilters
      );

      setTransactions(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Failed to load transactions');
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransactionStats = async () => {
    try {
      const response = await superadminTransactionsService.stats();
      setStats(response);
      console.log('Transaction stats:::::', response);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load transaction stats');
      setStats(null);
    }
  };

  const fetchMerchants = async () => {
    try {
      const res = await superadminTransactionsService.listMerchants();

      console.log('Merchant API response:', res); // 👈 keep once to verify

      // ✅ interceptor already returned data
      setMerchants(res.results || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch merchants');
    }
  };

  const handleFilterPanelOpen = () => {
    setIsFilterPanelOpen(true);
  };

  const handleFilterPanelClose = () => {
    setIsFilterPanelOpen(false);
  };

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = async () => {
    console.log('TransactionsPage - Received filters:', filters);
    setIsLoading(true);
    try {
      let startDate = '';
      let endDate = '';
      let status = '';
      let merchantUuids = '';

      // Apply date range filter if selected
      if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
        const formatDate = (dateStr: string) => {
          const date = new Date(dateStr);
          return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        };

        startDate = formatDate(filters.dateRange.startDate);
        endDate = formatDate(filters.dateRange.endDate);

        // console.log('Date range filter---:', { startDate, endDate });
      }

      // Apply status filter if selected
      if (filters.statusFilter) {
        const statuses = filters.statusFilter.split(',').filter(Boolean);
        if (statuses.length > 0) {
          status = encodeURIComponent(statuses.join(','));
        }
      }

      // 🟢 Merchants (IMPORTANT PART)
      if (filters.merchants && filters.merchants.length > 0) {
        merchantUuids = filters.merchants
          .map((m: any) => m.uuid) // or m.id based on API
          .join(',');
      }

      setAppliedFilters(prev => ({
        ...prev,
        ...(status && { transaction_status: status }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
        ...(merchantUuids && { merchant_uuid: merchantUuids }),
      }));

      console.log('appliedFilters:', appliedFilters);

      return;
    } catch (error) {
      console.error('Error applying filters:', error);
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setIsFilterPanelOpen(false);
    }
  };

  // Apply search filters
  const handleSearch = () => {
    const newFilters = {
      [searchKey]: searchValue.trim() || undefined,
    };

    setAppliedFilters(newFilters);
    setCurrentPage(1);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setAppliedFilters({});
    setFilters({
      searchTerm: '',
      statusFilter: '',
      dateRange: { startDate: '', endDate: '' },
      merchants: [],
    });

    setStatusFilter('');
    setSearchValue('');
    setSearchKey('transaction_id');

    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  // Format currency for display
  const formatTransactionCurrency = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return formatCurrency(numAmount);
  };

  // Format date for display
  const formatTransactionDate = (dateString: string) => {
    return formatMomentDate(dateString, 'DD MMM YYYY, h:mm A');
  };

  // Handle view transaction
  const handleViewTransaction = (transaction: SuperadminTransaction) => {
    setSelectedTransaction(transaction);
    setIsDrawerOpen(true);
  };

  // Get subtitle with count
  const getSubtitle = () => {
    return `${totalCount} ${totalCount === 1 ? 'transaction' : 'transactions'}`;
  };

  const handleRefresh = () => {
  fetchTransactions();
};


  const handleExport = () => {
    console.log('Export clicked');
  };

  // Breadcrumbs configuration
  const breadcrumbConfig = getBreadcrumbConfig('superadmin');
  const finalBreadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
  });

  // Header actions
  const headerActions: PageHeaderAction[] = [
    // {
    //   label: '',
    //   icon: <RefreshIcon />,
    //   onClick: handleRefresh,
    //   variant: 'text',
    //   size: 'small',
    // },
  ];

  return (
    <Box>
      <PageHeader
        title="Transactions"
        subtitle={getSubtitle()}
        breadcrumbs={finalBreadcrumbs}
        actions={headerActions}
        showDivider={false}
      />

      <SuperadminTransactionSummaryCards stats={stats} formatCurrency={formatTransactionCurrency} />

      <Box
        mb={3}
        display="flex"
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      >
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200, background: 'white' }}>
            <Select value={searchKey} onChange={e => setSearchKey(e.target.value)}>
              <MenuItem value="transaction_id">Transaction ID</MenuItem>
              <MenuItem value="merchant_transaction_id">Merchant Transaction ID</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            type="search"
            placeholder={`Enter ${searchKey.replace(/_/g, ' ')}`}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: 300,
              '& .MuiOutlinedInput-root': {
                background: 'white',
              },
            }}
          />
          {/* Clear Filters Button */}
          <Button variant="text" size="small" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Box>

        {/* <Button variant="outlined" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>
          Refresh
        </Button> */}
        <Box>
          <Button variant="outlined" size="small" onClick={handleExport} startIcon={<ExportIcon />}>
            Export
          </Button>
          <Button variant="text" size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </Button>
        </Box>
      </Box>
      <Box gap={1} mb={2}>
        {/* Filter Button */}
        <Button
          ref={filterButtonRef}
          variant="outlined"
          size="small"
          onClick={handleFilterPanelOpen}
          endIcon={<FilterIcon />}
          startIcon={<AddIcon />}
          sx={{
            minWidth: { xs: '100%', sm: 'auto' },
            textTransform: 'none',
          }}
        >
          Add Filters
        </Button>
        {isFilterPanelOpen && (
          <ClickAwayListener onClickAway={handleFilterPanelClose}>
            <Box>
              <FilterPanel
                open={isFilterPanelOpen}
                anchorEl={filterButtonRef.current}
                onClose={handleFilterPanelClose}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                merchants={merchants} // ✅ PASS HERE
              />
            </Box>
          </ClickAwayListener>
        )}
      </Box>

      {/* Transactions Table */}
      <Paper elevation={1}>
        {hasPermission('ADMIN_TRANSACTION_LIST_VIEW') ? (
          <Box >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Merchant ID</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Gateway</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="textSecondary">
                          No transactions found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map(transaction => (
                      <TableRow key={transaction.transaction_id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {/* <FingerprintIcon color="action" sx={{ fontSize: 16 }} /> */}
                            <Typography variant="body2" fontFamily="monospace">
                              {transaction.transaction_id}
                            </Typography>
                            <Tooltip title="Copy to clipboard">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigator.clipboard.writeText(String(transaction.transaction_id))
                                }
                                className="copy-icon"
                                sx={{ ml: 0.5, p: 0.25, opacity: 100, transition: 'opacity 0.2s' }}
                              >
                                <ContentCopyIcon sx={{ fontSize: '15px' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {transaction.merchant_transaction_id}
                          </Typography>
                          {/* <Tooltip title="Copy to clipboard">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigator.clipboard.writeText(String(transaction.transaction_id))
                            }
                            className="copy-icon"
                            sx={{ ml: 0.5, p: 0.25, opacity: 100, transition: 'opacity 0.2s' }}
                          >
                            <ContentCopyIcon sx={{ fontSize: '15px' }} />
                          </IconButton>
                        </Tooltip> */}
                        </TableCell>

                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {transaction.customer?.customer_name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {transaction.customer?.customer_email ||
                                transaction.customer?.customer_phone ||
                                'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ color: 'success.main' }}
                          >
                            {formatTransactionCurrency(transaction.transaction_amount)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={getSuperadminStatusLabel(transaction.transaction_status)}
                            color={getSuperadminStatusColor(transaction.transaction_status)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <BankIcon color="action" sx={{ fontSize: 16 }} />
                            <Typography variant="body2">
                              {transaction.payment_gateway?.payment_gateway_name || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {formatTransactionDate(transaction.transaction_date_time)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewTransaction(transaction)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 15, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={pageSize}
              page={currentPage - 1}
              onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
              onRowsPerPageChange={handlePageSizeChange}
            />
          </Box>
        ) : (
          <Typography sx={{p:4}} variant="body1" color="textSecondary">
            You do not have permission to view.
          </Typography>
        )}
      </Paper>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 600 },
            padding: 3,
            pt: 8,
            overflowY: 'auto',
          },
        }}
      >
        {selectedTransaction ? (
          <Box
            display="flex"
            flexDirection="column"
            gap={3}
            sx={{ position: 'relative' }} // <-- ADD THIS
          >
            <Box sx={{ position: 'relative' }}>
              <IconButton
                onClick={handleCloseDrawer}
                sx={{
                  position: 'absolute',
                  top: -24,
                  right: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            {/* Transaction Info */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 700 }}>Transaction ID:</Typography>
              <Typography sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                {selectedTransaction.transaction_id || 'N/A'}
              </Typography>
            </Paper>

            {/* Gateway Transaction ID */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 700 }}>Gateway Transaction ID:</Typography>
              <Typography sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                {selectedTransaction.gateway_transaction_id || 'N/A'}
              </Typography>
            </Paper>

            {/* Amount & Status */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box display="flex" flexDirection="column" gap={1.2}>
                <Box display="flex">
                  <Box width="40%" fontWeight={600}>
                    Amount
                  </Box>
                  <Box width="60%">{selectedTransaction.transaction_amount}</Box>
                </Box>
                <Box display="flex">
                  <Box width="40%" fontWeight={600}>
                    Status
                  </Box>
                  <Box width="60%">{selectedTransaction.transaction_status}</Box>
                </Box>
              </Box>
            </Paper>

            {/* CUSTOMER INFO */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 700, color: '#1A73E8', mb: 1, fontSize: '18px' }}>
                Customer Info
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.4}>
                {[
                  ['Name', selectedTransaction?.customer?.customer_name],
                  ['Phone', selectedTransaction?.customer?.customer_phone],
                  ['Email', selectedTransaction?.customer?.customer_email],
                  ['Address', selectedTransaction?.customer?.address],
                  ['City', selectedTransaction?.customer?.city],
                  ['Pincode', selectedTransaction?.customer?.pincode],
                  ['State', selectedTransaction?.customer?.state],
                ].map(([label, value]) => (
                  <Box key={label} display="flex">
                    <Box width="40%" fontWeight={600}>
                      {label}
                    </Box>
                    <Box width="60%">{value || 'N/A'}</Box>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* UDF Section */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 700, color: 'green', mb: 1, fontSize: '18px' }}>
                UDF Info
              </Typography>

              <Box display="flex" flexDirection="column" gap={1.2}>
                {Object.keys(selectedTransaction)
                  .filter(key => key.startsWith('udf'))
                  .map(key => (
                    <Box key={key} display="flex">
                      <Box width="40%" fontWeight={600}>
                        {key.toUpperCase()}
                      </Box>
                      <Box width="60%">{selectedTransaction[key] || 'N/A'}</Box>
                    </Box>
                  ))}
              </Box>
            </Paper>
          </Box>
        ) : (
          <Box>
            <Typography>Select a transaction to view details.</Typography>
            <Button variant="outlined" onClick={handleCloseDrawer} fullWidth>
              Close
            </Button>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default SuperadminTransactionsPage;
